import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { BrandButton } from "../../../components/ui/index.js";

const fmt = (n, currency = "EUR") =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: currency || "EUR" }).format(Number(n || 0));

const CheckoutSuccessPage = () => {
    const { orderNumber } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [order, setOrder] = useState(location.state?.order || null);
    const [loading, setLoading] = useState(!order);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (order || !orderNumber) return;
        let cancelled = false;
        setLoading(true);
        axios.get(`/orders/mine/${orderNumber}/`, { withCredentials: true })
            .then(res => { if (!cancelled) setOrder(res.data); })
            .catch(err => {
                if (cancelled) return;
                setError(err.response?.data?.detail || "No se pudo cargar el pedido.");
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [orderNumber, order]);

    if (loading) {
        return (
            <div className="h-full w-full flex items-center justify-center">
                <span className="loading loading-spinner loading-lg text-primary" />
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="h-full w-full flex items-center justify-center px-4">
                <div className="glassCard rounded-lg p-6 max-w-md text-center">
                    <h1 className="text-xl font-semibold mb-2">Pedido no disponible</h1>
                    <p className="text-sm text-base-content/70">{error || "No encontramos este pedido."}</p>
                    <BrandButton as="link" to="/profile/mis-pedidos" size="sm" className="mt-4">
                        Mis pedidos
                    </BrandButton>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full w-full overflow-y-scroll" style={{ minHeight: 0 }}>
            <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
                <div className="glassCard rounded-2xl p-6 md:p-10 flex flex-col items-center gap-4 text-center">
                    <CheckCircle2 className="w-14 h-14 text-success" />
                    <h1 className="text-2xl md:text-3xl font-bold">¡Pedido recibido!</h1>
                    <p className="text-sm text-base-content/70 max-w-md">
                        Tu pedido está registrado con estado <strong>{order.status_display || order.status}</strong>.
                        Te contactaremos por email o teléfono para confirmar pago y producción.
                    </p>

                    <div className="bg-base-200/40 rounded-xl px-4 py-3 text-sm">
                        <span className="text-base-content/60">Número de pedido</span>
                        <p className="font-mono text-base font-semibold">{order.order_number}</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-md text-left">
                        <div className="bg-base-200/30 rounded-lg p-3">
                            <span className="block text-xs text-base-content/50">Total</span>
                            <p className="font-bold">{fmt(order.total_price, order.currency)}</p>
                        </div>
                        {order.classification === "digital_only" ? (
                            <div className="bg-base-200/30 rounded-lg p-3">
                                <span className="block text-xs text-base-content/50">Entrega</span>
                                <p className="text-sm">Solo digital, sin envío físico</p>
                            </div>
                        ) : (
                            <div className="bg-base-200/30 rounded-lg p-3">
                                <span className="block text-xs text-base-content/50">Envío</span>
                                <p className="text-sm">{order.shipping_name}</p>
                                <p className="text-xs text-base-content/60">{order.shipping_city}, {order.shipping_postal_code}</p>
                            </div>
                        )}
                    </div>

                    {(order.classification === "digital_only" || order.classification === "mixed") && (
                        <p className="text-xs text-base-content/60 max-w-md">
                            El archivo digital de este pedido se habilitará para descarga cuando se confirme el pago, no ahora.
                        </p>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 mt-4">
                        <BrandButton as="link" to="/profile/mis-pedidos" variant="solid" size="md">
                            Ver mis pedidos
                        </BrandButton>
                        <BrandButton as="link" to="/store" variant="ghost" size="md">
                            Seguir comprando
                        </BrandButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutSuccessPage;
